import { useMipKycSetting } from '@dealstack/frontend/app/pages/Project/MIP/hooks/useKycSetting';
import { ManageInvestorDetailsContext } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Investors/InvestorDetails/InvestorDetails';
import { PhoneNumber } from '@dealstack/frontend/shared/components/PhoneNumber/PhoneNumber';
import { StrawberryTable } from '@dealstack/frontend/shared/components/Table/StrawberryTable/StrawberryTable';
import {
	TEST_ELEMENT_IDS,
	UIElementIdentifier,
} from '@dealstack/frontend/shared/helper/UIElementIdentifier/UIElementIdentifier';
import { useTranslationMipManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { LONG_DASH } from '@dealstack/frontend/shared/other/react/specialSigns.tsx';
import {
	getCountryNameByCode,
	getNationalityNamesByCodes,
} from '@dealstack/shared/lib/countries/countriesData.ts';
import { formatBirthday } from '@dealstack/shared/lib/format/dates/date';
import {
	AddressTag,
	CustomDetailDataTypeEnum,
	CustomDetailEnum,
} from '@dealstack/shared/lib/generated';
import { useContext, useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getPersonEmployment } from '@dealstack/frontend/app/services/Person.service.ts';
import API_PATHS from '@dealstack/shared/lib/generated/apiPaths.ts';
import styled, { css } from 'styled-components';
import { TableBodyCell } from '@dealstack/frontend/shared/components/Table/StrawberryTable/StrawberryTableBody.types';
import { ActionIcon, Badge, Flex } from '@mantine/core';
import { usePermissionRequest } from '@dealstack/frontend/shared/components/PermissionWrapper/PermissionWrapper.tsx';
import {
	PermissionRole,
	ResourceType,
} from '@dealstack/shared/lib/common/fineGrainedAuthorization/permissions.ts';
import {
	useClassifiersForInvestor,
	useDetachInvestorFromClassifier,
	useMipClassifiersList,
} from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ProgrammeSettings/Classifiers/hooks/useMipClassifiers.ts';
import { ManageInvestorMipClassifiersDrawer } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Investors/InvestorDetails/components/ManageInvestorMipClassifiersDrawer/ManageInvestorMipClassifiersDrawer.tsx';
import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon';
import { showToastV2 } from '@dealstack/frontend/shared/mechanism/toast/useToastV2.tsx';
import { useRequiredParams } from '@dealstack/frontend/shared/utils/useRequiredParams.ts';

const PAGE_WRAPPER_PADDING = 2 * 28; //px
const PARENT_WRAPPER_PADDING = 2 * 16; //px
const GAP_IN_PARENT_COMPONENT = 16; //px
const TABLE_COLUMN_WIDTH_WITH_MARGINS = 220; //px

const InvestorPersonalInformationSection = ({ className }: { className?: string }) => {
	const manageInvestorDetailsController = useContext(ManageInvestorDetailsContext);
	const { investorDetailsData, investorId } = manageInvestorDetailsController;
	const { mipId } = useRequiredParams();
	const tMipManage = useTranslationMipManage();
	const { data: mipSettings } = useMipKycSetting(mipId ?? '');
	const [isDrawerOpen, setIsDrawerOpen] = useState(false);

	const personData = investorDetailsData?.person;
	const mipCustomDetails = (investorDetailsData?.mipCustomDetails ?? []).filter(
		(cd) =>
			cd.dataType !== CustomDetailDataTypeEnum.BANK_ACCOUNT &&
			cd.dataType !== CustomDetailDataTypeEnum.FORM &&
			cd.dataType !== CustomDetailDataTypeEnum.PRE_FILLABLE_FORM &&
			cd.isChecked &&
			cd.type === CustomDetailEnum.INDIVIDUAL
	);
	const { data: employmentRecords } = useQuery({
		queryKey: [API_PATHS.PERSON_PERSONID_EMPLOYMENT_GET, personData?.id],
		queryFn: () => getPersonEmployment(personData?.id ?? ''),
		enabled: !!mipSettings?.isEmployeeIdRequired && !!personData?.id,
	});
	const employmentData = employmentRecords?.[0];

	const taxResidenceWithTin = personData?.taxResidence.find((t) => !!t.number);
	const firstTaxResidence = personData?.taxResidence[0];
	const personTaxResidence = taxResidenceWithTin ?? firstTaxResidence;

	const investorCustomDetailsName = mipCustomDetails.map(
		(investorCustomDetail) => investorCustomDetail.name
	);

	const investorCustomDetailsValue = mipCustomDetails.map(
		(customDetail) =>
			investorDetailsData?.investorCustomDetails.find(
				(id) => id.customDetailId === customDetail.id
			)?.value || LONG_DASH
	);

	const { hasPermission: canEdit } = usePermissionRequest({
		resourceType: ResourceType.PROGRAMME,
		resourceId: mipId,
		permissionRole: PermissionRole.EDITOR,
	});

	const {
		data: assignedMipClassifiers = [],
		isLoading: isLoadingAssigned,
		refetch: refetchAssignedMipClassifiers,
	} = useClassifiersForInvestor({ investorId: investorId ?? '' });

	const { data: allActiveMipClassifiers = [] } = useMipClassifiersList({
		mipId,
		isArchived: false,
	});

	const { mutate: detach, isPending: detachLoading } = useDetachInvestorFromClassifier({
		investorId: investorId ?? '',
	});

	const assignedIds = useMemo(
		() => new Set(assignedMipClassifiers.map((c) => c.id)),
		[assignedMipClassifiers]
	);

	const availableMipClassifiersToAdd = useMemo(
		() => allActiveMipClassifiers.filter((c) => !assignedIds.has(c.id)),
		[allActiveMipClassifiers, assignedIds]
	);

	const handleRemove = (mipClassifierId: string) => {
		if (!investorId) {
			showToastV2({ message: 'Missing investor id', state: 'error' });
			return;
		}

		detach({ mipClassifierId, body: { investorIds: [investorId] } });
	};

	const investorPersonalInformationHeaderArray = useMemo(
		() => [
			tMipManage('investors.expanded_row.phone_number'),
			tMipManage('investors.expanded_row.address'),
			tMipManage('investors.expanded_row.date_of_birth'),
			tMipManage('root.investor.mip_classifiers.title'),
			...(mipSettings?.isTaxResidenceRequired
				? [tMipManage('investors.expanded_row.tax_residency')]
				: []),
			...(mipSettings?.isTinRequired ? [tMipManage('investors.expanded_row.tin')] : []),
			...(mipSettings?.isPlaceOfBirthRequired
				? [tMipManage('investors.expanded_row.place_of_birth')]
				: []),
			...(mipSettings?.isCountryOfBirthRequired
				? [
						tMipManage('investors.expanded_row.country_of_birth', {
							defaultValue: 'Country of birth',
						}),
					]
				: []),
			...(mipSettings?.isEmployeeIdRequired
				? [
						tMipManage('investors.expanded_row.employer'),
						tMipManage('investors.expanded_row.employee_id'),
					]
				: []),
			...(mipSettings?.isCitizenshipRequired
				? [tMipManage('investors.expanded_row.citizenship')]
				: []),
		],
		[tMipManage, mipSettings]
	);

	const parsedInvestorPersonalInformationHeaderValue = useMemo(
		() =>
			investorCustomDetailsName?.length
				? [...investorPersonalInformationHeaderArray, ...investorCustomDetailsName]
				: investorPersonalInformationHeaderArray,
		[investorCustomDetailsName, investorPersonalInformationHeaderArray]
	);

	const cohortsCellContent = useMemo(
		() => (
			<Flex gap="0.25rem" wrap="wrap">
				{assignedMipClassifiers.map((mipClassifier) => (
					<Flex key={mipClassifier.id}>
						<Badge
							c="dark.4"
							color="gray.4"
							fw={400}
							variant="outline"
							size="sm"
							rightSection={
								canEdit && (
									<ActionIcon
										disabled={detachLoading}
										size="xs"
										variant="transparent"
										c="dark.8"
										onClick={() => handleRemove(mipClassifier.id)}
									>
										<Icon variant="close-v3" size="sm" />
									</ActionIcon>
								)
							}
						>
							{mipClassifier.name}
						</Badge>
					</Flex>
				))}
				{canEdit && (
					<ActionIcon
						size="24px"
						variant="default"
						bg="transparent"
						disabled={isLoadingAssigned}
						onClick={() => setIsDrawerOpen(true)}
						radius="lg"
					>
						<Icon variant="add" size="sm" />
					</ActionIcon>
				)}
			</Flex>
		),
		[assignedMipClassifiers, canEdit, isLoadingAssigned, detachLoading]
	);

	const personAddress =
		personData?.addresses?.find((a) => a?.tags?.includes(AddressTag.PRIMARY_ADDRESS)) ??
		personData?.addresses?.[0];
	const { houseNumber, street, city, country, postCode } = personAddress ?? {};
	const personFormattedAddress = [street, houseNumber, city, postCode, country]
		.filter((item) => item && item?.trim())
		.join(', ');

	const investorPersonInformationBodyArray = useMemo(
		() => [
			personData?.telephonePrefix && personData?.phoneNumber
				? {
						element: (
							<UIElementIdentifier
								identifier={
									TEST_ELEMENT_IDS.MIP_MANAGE_INVESTORS_DETAILS_PHONE_NUMBER
								}
							>
								<PhoneNumber
									telephonePrefix={personData?.telephonePrefix}
									phoneNumber={personData?.phoneNumber}
								/>
							</UIElementIdentifier>
						),
						textToCopy: personData?.telephonePrefix + personData?.phoneNumber,
					}
				: {
						element: <>&mdash;</>,
					},
			personFormattedAddress || {
				element: <>&mdash;</>,
			},
			formatBirthday(personData?.dateOfBirth) ?? {
				element: <>&mdash;</>,
			},
			{
				element: cohortsCellContent,
			},
			...(mipSettings?.isTaxResidenceRequired
				? [personTaxResidence?.countryCode || LONG_DASH]
				: []),
			...(mipSettings?.isTinRequired ? [personTaxResidence?.number || LONG_DASH] : []),
			...(mipSettings?.isPlaceOfBirthRequired ? [personData?.placeOfBirth || LONG_DASH] : []),
			...(mipSettings?.isCountryOfBirthRequired
				? [getCountryNameByCode(personData?.countryOfBirth) || LONG_DASH]
				: []),
			...(mipSettings?.isEmployeeIdRequired
				? [
						employmentData?.employerName || LONG_DASH,
						employmentData?.employeeReference || LONG_DASH,
					]
				: []),
			...(mipSettings?.isCitizenshipRequired
				? [getNationalityNamesByCodes(personData?.citizenship).join(', ') || LONG_DASH]
				: []),
		],
		[
			personData,
			personFormattedAddress,
			mipSettings,
			personTaxResidence,
			employmentData,
			assignedMipClassifiers,
			canEdit,
			isLoadingAssigned,
			detachLoading,
		]
	);

	const parsedInvestorPersonDetailsBodyValue = investorCustomDetailsValue?.length
		? [...investorPersonInformationBodyArray, ...investorCustomDetailsValue]
		: investorPersonInformationBodyArray;

	const calculateAvailableWidthForTable = () => {
		const parentContainerMaxWidth = window.innerWidth;

		const availableWidth =
			parentContainerMaxWidth -
			PAGE_WRAPPER_PADDING -
			PARENT_WRAPPER_PADDING -
			GAP_IN_PARENT_COMPONENT;
		return availableWidth;
	};

	const [availableWidth, setAvailableWidth] = useState(calculateAvailableWidthForTable());

	useEffect(() => {
		const handleResize = () => {
			setAvailableWidth(calculateAvailableWidthForTable());
		};

		window.addEventListener('resize', handleResize);

		return () => {
			window.removeEventListener('resize', handleResize);
		};
	}, [calculateAvailableWidthForTable]);

	const tablesForPerson = useMemo(() => {
		const tables: { header: string[]; body: TableBodyCell[]; width: number }[] = [];
		let currentTable: { header: string[]; body: TableBodyCell[]; width: number } = {
			header: [],
			body: [],
			width: 0,
		};

		for (let i = 0; i < parsedInvestorPersonalInformationHeaderValue.length; i++) {
			if (currentTable.width + TABLE_COLUMN_WIDTH_WITH_MARGINS <= availableWidth) {
				currentTable.header.push(parsedInvestorPersonalInformationHeaderValue[i]);
				currentTable.body.push(parsedInvestorPersonDetailsBodyValue[i]);
				currentTable.width += TABLE_COLUMN_WIDTH_WITH_MARGINS;
			} else {
				tables.push(currentTable);
				currentTable = {
					header: [parsedInvestorPersonalInformationHeaderValue[i]],
					body: [parsedInvestorPersonDetailsBodyValue[i]],
					width: TABLE_COLUMN_WIDTH_WITH_MARGINS,
				};
			}
		}

		if (currentTable.header.length > 0) {
			tables.push(currentTable);
		}

		return tables;
	}, [
		availableWidth,
		parsedInvestorPersonalInformationHeaderValue,
		parsedInvestorPersonDetailsBodyValue,
	]);

	return (
		<div className={className}>
			<InvestorPersonalInformationSmallWrapper $shouldWrap={tablesForPerson?.length > 1}>
				<Wrapper>
					{tablesForPerson.map((table, index) => {
						return (
							<StrawberryTable
								key={index}
								header={table.header}
								body={table.body}
								hasUpperHorizontalLine={index < tablesForPerson.length - 1}
								hasBottomHorizontalLine={index === tablesForPerson.length - 2}
							/>
						);
					})}
				</Wrapper>
			</InvestorPersonalInformationSmallWrapper>

			<ManageInvestorMipClassifiersDrawer
				investorId={investorId ?? ''}
				investorName={investorDetailsData?.person?.displayName ?? ''}
				availableMipClassifiers={availableMipClassifiersToAdd}
				hasPermission={canEdit}
				opened={isDrawerOpen}
				onClose={() => setIsDrawerOpen(false)}
				onSuccess={refetchAssignedMipClassifiers}
			/>
		</div>
	);
};

const Wrapper = styled.div`
	display: flex;
	flex-wrap: wrap;
	width: 100%;
`;

const InvestorPersonalInformationSmallWrapper = styled.div<{ $shouldWrap?: boolean }>`
	display: flex;
	padding: 0 1rem 0;
	gap: 1rem;

	${({ $shouldWrap }) =>
		$shouldWrap
			? css`
					align-items: flex-start;
					flex-direction: column;
					padding: 1rem;
				`
			: css`
					align-items: center;
				`};
`;

const Styled = styled(InvestorPersonalInformationSection)`
	position: relative;
	background-color: ${({ theme }) => theme.v2.color.neutral[100]};
	margin-bottom: 2rem;
	${StrawberryTable} {
		padding-left: 0;
	}
`;

export { Styled as InvestorPersonalDetailsSection };
